import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import BlockIcon from '@mui/icons-material/Block';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddLinkIcon from '@mui/icons-material/AddLink';
import SearchIcon from '@mui/icons-material/Search';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  cancelPaymentLink,
  createPaymentLink,
  getPaymentLinkAvailability,
  getPaymentLinks,
  sendPaymentLinkSms,
  type PaymentLinkDto,
} from '../../../services/ecommerceApi';

interface PaymentLinksDialogProps {
  open: boolean;
  onClose: () => void;
  /** Verilirse yalnızca bu carinin linkleri; verilmezse tüm linkler. */
  accountId?: number | string | null;
  accountName?: string | null;
  /** Tek cari modunda yeni link formuna önerilen cep telefonu. */
  accountPhone?: string | null;
  /** Tüm linkler modunda yeni link için cari seçimi. */
  accountOptions?: PaymentLinkAccountOption[];
}

export interface PaymentLinkAccountOption {
  id: number | string;
  name: string;
  phone?: string;
}

interface CreateForm {
  account: PaymentLinkAccountOption | null;
  amount: string;
  description: string;
  phone: string;
}

type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

const REFRESH_MS = 15000;
const PAGE_SIZES = [25, 50, 100];
const DEFAULT_DAYS = 30;

/** yyyy-mm-dd (yerel) */
const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateInput(d);
};

/** Yerel gün başlangıcı → ISO (UTC); `nextDay` bitiş tarihi için (o gün dahil). */
const dayStartIso = (day: string, nextDay = false) => {
  const d = new Date(`${day}T00:00:00`);
  if (nextDay) d.setDate(d.getDate() + 1);
  return d.toISOString();
};

/**
 * Cari tahsilat ödeme linkleri (Pavo "Sanal POS" ve panel).
 * - Cari satırındaki 3 nokta: o carinin linkleri.
 * - "Yeni Cari Ekle" yanındaki 3 nokta: tüm linkler.
 * İşlemler: yeni link, kopyala, QR, SMS ile (tekrar) gönder, iptal, dekont.
 */
const PaymentLinksDialog: React.FC<PaymentLinksDialogProps> = ({
  open, onClose, accountId, accountName, accountPhone, accountOptions,
}) => {
  const { t } = useTranslation('accounting');
  const { userDetails } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const single = accountId != null && accountId !== '';

  const [links, setLinks] = useState<PaymentLinkDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  // Tüm linklerde varsayılan son 30 gün; tek caride tarih sınırı yok.
  const defaultFrom = single ? '' : daysAgo(DEFAULT_DAYS);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [qrLink, setQrLink] = useState<PaymentLinkDto | null>(null);
  const [smsLink, setSmsLink] = useState<PaymentLinkDto | null>(null);
  const [smsPhone, setSmsPhone] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [createForm, setCreateForm] = useState<CreateForm | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  // Arama yazarken her tuşta istek atma.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Filtre değişince ilk sayfa.
  useEffect(() => { setPage(0); }, [statusFilter, sourceFilter, fromDate, toDate, search, pageSize, accountId]);

  const filtered = Boolean(statusFilter || sourceFilter || fromDate || toDate || search);

  const clearFilters = () => {
    setStatusFilter('');
    setSourceFilter('');
    setFromDate(defaultFrom);
    setToDate('');
    setSearchInput('');
    setSearch('');
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getPaymentLinks({
        accountId: single ? accountId : null,
        status: statusFilter,
        source: sourceFilter,
        from: fromDate ? dayStartIso(fromDate) : undefined,
        to: toDate ? dayStartIso(toDate, true) : undefined,
        search,
        page: page + 1,
        pageSize,
      });
      setLinks(res.items);
      setTotal(res.total);
      setError(null);
    } catch (err: any) {
      setError(t('current.paymentLinks.loadFailed', { message: err?.response?.data?.message || err?.message || '' }));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [accountId, single, t, statusFilter, sourceFilter, fromDate, toDate, search, page, pageSize]);

  useEffect(() => {
    if (!open) return;
    void load();
    const timer = setInterval(() => void load(true), REFRESH_MS);
    return () => clearInterval(timer);
  }, [open, load]);

  const formatMoney = (amount: number, currency: string) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency || 'TRY' }).format(amount);

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';

  const statusChip = (l: PaymentLinkDto) => {
    let key: string = l.status;
    let color: ChipColor = 'default';
    if (l.status === 'Paid') color = 'success';
    else if (l.status === 'Pending') {
      if (l.paymentState === 'received') { key = 'received'; color = 'success'; }
      else if (l.paymentState === 'failed') { key = 'failed'; color = 'error'; }
      else if (l.paymentState === 'open') { key = 'open'; color = 'info'; }
      else color = 'warning';
    }
    const chip = (
      <Chip size="small" color={color} variant={l.status === 'Paid' ? 'filled' : 'outlined'}
        label={t(`current.paymentLinks.status.${key}`)} />
    );
    return key === 'failed' && l.lastFailReason
      ? <Tooltip title={t('current.paymentLinks.failReason', { reason: l.lastFailReason })}>{chip}</Tooltip>
      : chip;
  };

  const copy = async (l: PaymentLinkDto) => {
    if (!l.url) return;
    try {
      await navigator.clipboard.writeText(l.url);
      setToast(t('current.paymentLinks.actions.copied'));
    } catch { /* izin yoksa sessiz */ }
  };

  const copyDekontNo = async (l: PaymentLinkDto) => {
    if (!l.dekontNo) return;
    try {
      await navigator.clipboard.writeText(l.dekontNo);
      setToast(t('current.paymentLinks.dekontNoCopied'));
    } catch { /* izin yoksa sessiz */ }
  };

  const cancel = async (l: PaymentLinkDto) => {
    if (!window.confirm(t('current.paymentLinks.cancelConfirm', { amount: formatMoney(l.amount, l.currency) }))) return;
    setBusyId(l.id);
    try {
      await cancelPaymentLink(l.id);
      await load(true);
    } catch (err: any) {
      setToast(t('current.paymentLinks.cancelFailed', { message: err?.response?.data?.message || err?.message || '' }));
      await load(true);
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = async () => {
    setCreateError(null);
    setCreateForm({
      account: single ? { id: accountId as number | string, name: accountName || '', phone: accountPhone || '' } : null,
      amount: '',
      description: '',
      phone: single ? (accountPhone || '') : '',
    });
    setAvailabilityLoading(true);
    try {
      const a = await getPaymentLinkAvailability();
      if (!a.available) {
        setCreateError(t('current.paymentLinks.create.unavailable', { message: a.message || a.code || '' }));
      }
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || err?.message || '');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const parsedAmount = (() => {
    if (!createForm) return 0;
    const n = Number(createForm.amount.replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  })();

  const submitCreate = async () => {
    if (!createForm?.account || parsedAmount <= 0) return;
    setCreating(true);
    setCreateError(null);
    try {
      const phone = createForm.phone.trim();
      const link = await createPaymentLink({
        accountId: createForm.account.id,
        amount: parsedAmount,
        description: createForm.description.trim() || null,
        phone: phone || null,
        createdByName: userDetails?.email || null,
      });
      setCreateForm(null);
      await load(true);
      // Oluşan link: QR + kopyala/aç/SMS penceresi
      setQrLink(link);
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || err?.message || '');
    } finally {
      setCreating(false);
    }
  };

  const openSms = (l: PaymentLinkDto) => {
    setSmsPhone(l.phone || '');
    setSmsLink(l);
  };

  const sendSms = async () => {
    if (!smsLink) return;
    const link = smsLink;
    setSmsLink(null);
    setBusyId(link.id);
    try {
      const res = await sendPaymentLinkSms(link.id, smsPhone.trim());
      setToast(res.sent ? t('current.paymentLinks.smsDialog.sent') : t('current.paymentLinks.smsDialog.notSent'));
      await load(true);
    } catch (err: any) {
      setToast(t('current.paymentLinks.smsDialog.failed', { message: err?.response?.data?.message || err?.message || '' }));
    } finally {
      setBusyId(null);
    }
  };

  const title = single
    ? t('current.paymentLinks.titleAccount', { name: accountName || '' })
    : t('current.paymentLinks.titleAll');

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={fullScreen}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode2Icon color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>{title}</Typography>
            <Typography variant="caption" color="text.secondary">{t('current.paymentLinks.subtitle')}</Typography>
          </Box>
          <Button variant="contained" size="small" startIcon={<AddLinkIcon />} onClick={() => void openCreate()}>
            {t('current.paymentLinks.create.button')}
          </Button>
          <Tooltip title={t('current.paymentLinks.refresh')}>
            <span>
              <IconButton onClick={() => void load()} disabled={loading}><RefreshIcon /></IconButton>
            </span>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label={t('current.paymentLinks.filters.search')}
              placeholder={t('current.paymentLinks.filters.searchHint')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ flex: '1 1 220px', minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <TextField
              select
              size="small"
              label={t('current.paymentLinks.filters.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">{t('current.paymentLinks.filters.statusAll')}</MenuItem>
              <MenuItem value="active">{t('current.paymentLinks.filters.statusActive')}</MenuItem>
              <MenuItem value="paid">{t('current.paymentLinks.filters.statusPaid')}</MenuItem>
              <MenuItem value="cancelled">{t('current.paymentLinks.filters.statusCancelled')}</MenuItem>
              <MenuItem value="expired">{t('current.paymentLinks.filters.statusExpired')}</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label={t('current.paymentLinks.filters.source')}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">{t('current.paymentLinks.filters.sourceAll')}</MenuItem>
              <MenuItem value="pavo">{t('current.paymentLinks.source.pavo')}</MenuItem>
              <MenuItem value="web">{t('current.paymentLinks.source.web')}</MenuItem>
            </TextField>
            <TextField
              type="date"
              size="small"
              label={t('current.paymentLinks.filters.from')}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toDate || undefined }}
            />
            <TextField
              type="date"
              size="small"
              label={t('current.paymentLinks.filters.to')}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: fromDate || undefined }}
            />
            <Button size="small" onClick={clearFilters}>{t('current.paymentLinks.filters.clear')}</Button>
          </Box>
          {loading && links.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : links.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              {t(filtered ? 'current.paymentLinks.emptyFiltered' : 'current.paymentLinks.empty')}
            </Typography>
          ) : (
            <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('current.paymentLinks.columns.date')}</TableCell>
                    {!single && <TableCell>{t('current.paymentLinks.columns.account')}</TableCell>}
                    <TableCell align="right">{t('current.paymentLinks.columns.amount')}</TableCell>
                    <TableCell>{t('current.paymentLinks.columns.status')}</TableCell>
                    <TableCell>{t('current.paymentLinks.columns.createdBy')}</TableCell>
                    <TableCell align="right">{t('current.paymentLinks.columns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {links.map((l) => {
                    const pending = l.status === 'Pending' && l.paymentState !== 'received';
                    const busy = busyId === l.id;
                    return (
                      <TableRow key={l.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(l.createdAt)}</TableCell>
                        {!single && <TableCell>{l.accountName || '-'}</TableCell>}
                        <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {formatMoney(l.amount, l.currency)}
                          {l.description && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 180 }}>
                              {l.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {statusChip(l)}
                          {l.paidAt && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatDate(l.paidAt)}
                            </Typography>
                          )}
                          {l.dekontNo && (
                            <Tooltip title={t('current.paymentLinks.dekontNoCopy')}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                noWrap
                                onClick={() => void copyDekontNo(l)}
                                sx={{ maxWidth: 200, cursor: 'pointer', fontFamily: 'monospace' }}
                              >
                                {t('current.paymentLinks.dekontNo', { no: l.dekontNo })}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip size="small" variant="outlined"
                              label={t(`current.paymentLinks.source.${l.createdSource === 'pavo' ? 'pavo' : 'web'}`)} />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>{l.createdBy || ''}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {busy ? <CircularProgress size={20} /> : (
                            <>
                              <Tooltip title={t('current.paymentLinks.actions.copy')}>
                                <span><IconButton size="small" disabled={!l.url} onClick={() => void copy(l)}><ContentCopyIcon fontSize="small" /></IconButton></span>
                              </Tooltip>
                              {/* Ödenmiş link mağaza sayfasında "Ödeme tamamlandı" + müşteriye verilen dekontu açar. */}
                              {l.status === 'Paid' && l.url && (
                                <Tooltip title={t('current.paymentLinks.actions.receipt')}>
                                  <IconButton size="small" color="success" href={l.url} target="_blank" rel="noopener noreferrer"><ReceiptLongIcon fontSize="small" /></IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={t('current.paymentLinks.actions.open')}>
                                <span><IconButton size="small" disabled={!l.url} href={l.url || ''} target="_blank" rel="noopener noreferrer"><OpenInNewIcon fontSize="small" /></IconButton></span>
                              </Tooltip>
                              {pending && (
                                <>
                                  <Tooltip title={t('current.paymentLinks.actions.qr')}>
                                    <span><IconButton size="small" disabled={!l.url} onClick={() => setQrLink(l)}><QrCode2Icon fontSize="small" /></IconButton></span>
                                  </Tooltip>
                                  <Tooltip title={t('current.paymentLinks.actions.sms')}>
                                    <IconButton size="small" onClick={() => openSms(l)}><SmsOutlinedIcon fontSize="small" /></IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('current.paymentLinks.actions.cancel')}>
                                    <IconButton size="small" color="error" onClick={() => void cancel(l)}><BlockIcon fontSize="small" /></IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
              rowsPerPageOptions={PAGE_SIZES}
              labelRowsPerPage={t('current.paymentLinks.filters.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('current.paymentLinks.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* QR */}
      <Dialog open={Boolean(qrLink)} onClose={() => setQrLink(null)}>
        <DialogTitle>{qrLink ? formatMoney(qrLink.amount, qrLink.currency) : ''}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {qrLink?.url && <QRCodeSVG value={qrLink.url} size={240} includeMargin />}
          <Typography variant="body2" color="text.secondary" textAlign="center">{qrLink?.accountName}</Typography>
          <Typography variant="caption" color="text.secondary" textAlign="center">{t('current.paymentLinks.qrHint')}</Typography>
          {qrLink?.url && (
            <TextField
              fullWidth
              size="small"
              value={qrLink.url}
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
              sx={{ mt: 1, minWidth: 300 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {qrLink && (
            <>
              <Button startIcon={<ContentCopyIcon />} disabled={!qrLink.url} onClick={() => void copy(qrLink)}>
                {t('current.paymentLinks.actions.copy')}
              </Button>
              <Button startIcon={<SmsOutlinedIcon />} onClick={() => { const l = qrLink; setQrLink(null); openSms(l); }}>
                {t('current.paymentLinks.actions.sms')}
              </Button>
              <Button startIcon={<OpenInNewIcon />} disabled={!qrLink.url} href={qrLink.url || ''} target="_blank" rel="noopener noreferrer">
                {t('current.paymentLinks.actions.open')}
              </Button>
            </>
          )}
          <Button onClick={() => setQrLink(null)}>{t('current.paymentLinks.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Yeni link */}
      <Dialog open={Boolean(createForm)} onClose={() => !creating && setCreateForm(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddLinkIcon color="primary" />
          {t('current.paymentLinks.create.title')}
        </DialogTitle>
        <DialogContent>
          {availabilityLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>}
          {createError && <Alert severity="error" sx={{ mb: 1 }}>{createError}</Alert>}
          {createForm && (
            <>
              {single ? (
                <TextField fullWidth margin="dense" label={t('current.paymentLinks.create.account')}
                  value={createForm.account?.name || ''} InputProps={{ readOnly: true }} />
              ) : (
                <Autocomplete
                  options={accountOptions || []}
                  value={createForm.account}
                  onChange={(_, v) => setCreateForm((f) => f && ({
                    ...f,
                    account: v,
                    phone: f.phone || v?.phone || '',
                  }))}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                  renderInput={(params) => (
                    <TextField {...params} margin="dense" label={t('current.paymentLinks.create.account')} autoFocus />
                  )}
                />
              )}
              <TextField
                fullWidth
                margin="dense"
                label={t('current.paymentLinks.create.amount')}
                value={createForm.amount}
                onChange={(e) => setCreateForm((f) => f && ({ ...f, amount: e.target.value.replace(/[^0-9.,]/g, '') }))}
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
                autoFocus={single}
              />
              <TextField
                fullWidth
                margin="dense"
                label={t('current.paymentLinks.create.description')}
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => f && ({ ...f, description: e.target.value.slice(0, 500) }))}
              />
              <TextField
                fullWidth
                margin="dense"
                type="tel"
                label={t('current.paymentLinks.create.phone')}
                helperText={t('current.paymentLinks.create.phoneHint')}
                placeholder="05XX XXX XX XX"
                value={createForm.phone}
                onChange={(e) => setCreateForm((f) => f && ({ ...f, phone: e.target.value }))}
              />
              <Typography variant="caption" color="text.secondary">{t('current.paymentLinks.create.hint')}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateForm(null)} disabled={creating}>{t('current.paymentLinks.smsDialog.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void submitCreate()}
            disabled={creating || availabilityLoading || !createForm?.account || parsedAmount <= 0}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddLinkIcon />}
          >
            {t('current.paymentLinks.create.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SMS */}
      <Dialog open={Boolean(smsLink)} onClose={() => setSmsLink(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('current.paymentLinks.smsDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            type="tel"
            label={t('current.paymentLinks.smsDialog.phone')}
            placeholder="05XX XXX XX XX"
            value={smsPhone}
            onChange={(e) => setSmsPhone(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmsLink(null)}>{t('current.paymentLinks.smsDialog.cancel')}</Button>
          <Button variant="contained" onClick={() => void sendSms()} disabled={!smsPhone.trim()}>
            {t('current.paymentLinks.smsDialog.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} message={toast || ''} />
    </>
  );
};

export default PaymentLinksDialog;
